import { useReactiveVar } from '@apollo/client'
import {
  CameraAltOutlined as CameraEnhanceOutlinedIcon,
  Close as CloseIcon,
  DevicesOutlined as DevicesOutlinedIcon,
  ImageOutlined as PhotoAlbumOutlinedIcon,
  SortOutlined as SortOutlinedIcon
} from '@mui/icons-material'
import { Box, Button, Dialog, IconButton, styled, Typography } from '@mui/material'
import { useState } from 'react'
import { modalVar } from '../../../../providers/apollo/cache'
import PaymentModal from './PaymentModal'

// Styled components for the modal
const StyledDialog = styled(Dialog)(() => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    overflow: 'hidden',
    maxWidth: '550px',
    width: '100%',
    margin: '16px',
    '@media (min-width: 768px)': {
      maxWidth: '600px'
    }
  }
}))

const HeaderSection = styled(Box)(({ theme }) => ({
  background: theme.palette.groupsy.gradient,
  padding: '125px 24px 40px',
  position: 'relative',
  textAlign: 'center',
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: '-1px',
    left: 0,
    right: 0,
    height: '70px',
    width: '100%',
    backgroundImage: 'url(https://content.photobucket.com/img/freePlan/freemium-modal-curve.png)',
    backgroundSize: 'cover',
    backgroundPosition: 'bottom center',
    backgroundRepeat: 'no-repeat'
  }
}))

const ConfettiContainer = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  pointerEvents: 'none',
  zIndex: 10
})

const ContentSection = styled(Box)({
  background: '#29344A',
  padding: '32px 20%',
  color: 'white',
  marginTop: '-1px'
})

const FeatureGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '24px',
  margin: '24px 0'
})

const FeatureItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px'
})

const FeatureIcon = styled(Box)(() => ({
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white'
}))

const UpgradeButton = styled(Button)({
  borderRadius: '25px',
  padding: '12px 32px',
  fontSize: '16px',
  fontWeight: 'bold',
  textTransform: 'none',
  width: '100%',
  marginTop: '24px',
  '&:hover': {
    background: '#1976d2'
  }
})

const CancelText = styled(Typography)({
  textAlign: 'center',
  marginTop: '12px',
  fontSize: '14px',
  opacity: 0.8,
  color: 'white'
})

const CloseButton = styled(IconButton)({
  position: 'absolute',
  top: '16px',
  right: '16px',
  color: 'black',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'transparent'
  }
})

export default function UpsellModal() {
  const modal = useReactiveVar(modalVar)
  const [showPaymentModal, setShowPaymentModal] = useState(false)

  const handleClose = () => {
    modalVar(null)
    setShowPaymentModal(false)
  }

  const handleUpgrade = () => {
    setShowPaymentModal(true)
  }

  const handlePaymentClose = () => {
    setShowPaymentModal(false)
  }

  const features = [
    { icon: 'DevicesOutlinedIcon', text: 'Unlimited Buckets' },
    { icon: 'SortOutlinedIcon', text: 'Sorting tools' },
    { icon: 'PhotoAlbumOutlinedIcon', text: '1 TB of storage' },
    { icon: 'CameraEnhanceOutlinedIcon', text: 'Bulk download' }
  ]

  return (
    <>
      <StyledDialog open={modal === 'upsellModal'} onClose={handleClose}>
        <HeaderSection>
          <CloseButton onClick={handleClose} size="small">
            <CloseIcon />
          </CloseButton>

          <ConfettiContainer>
            <img
              src="https://content.photobucket.com/img/freePlan/crown.png"
              alt="Crown"
              style={{
                width: '160px',
                position: 'absolute',
                left: '50%',
                bottom: '-10px',
                transform: 'translateX(-50%)'
              }}
            />
          </ConfettiContainer>
        </HeaderSection>
        <ContentSection>
          <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 'bold' }}>
            This is a premium feature - unlock for just $8/month
          </Typography>
          <FeatureGrid>
            {features.map((feature, index) => {
              const iconMap = {
                CameraEnhanceOutlinedIcon,
                DevicesOutlinedIcon,
                SortOutlinedIcon,
                PhotoAlbumOutlinedIcon
              }
              const IconComponent = iconMap[feature.icon as keyof typeof iconMap]

              return (
                // eslint-disable-next-line react/no-array-index-key
                <FeatureItem key={`feature-${index}`}>
                  <FeatureIcon>
                    <IconComponent sx={{ fontSize: '16px' }} />
                  </FeatureIcon>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {feature.text}
                  </Typography>
                </FeatureItem>
              )
            })}
          </FeatureGrid>
        </ContentSection>
        <Box sx={{ background: '#29344A', padding: '0 40px 32px', marginTop: '-1px' }}>
          <UpgradeButton variant="contained" color="primary" onClick={handleUpgrade}>
            Upgrade Now
          </UpgradeButton>
          <CancelText>Cancel Anytime</CancelText>
        </Box>
      </StyledDialog>
      <PaymentModal
        open={showPaymentModal}
        onClose={handlePaymentClose}
        planName="sharing"
        billingCycle="monthly"
        total="8.00"
        buttonText="Complete Upgrade"
      />
    </>
  )
}

// Helper function to open the modal programmatically
export const openUpsellModal = () => {
  modalVar('upsellModal')
}
